package com.elan.edubridge.shared.domain.policy

import com.elan.edubridge.shared.domain.model.AgeGroup
import com.elan.edubridge.shared.domain.model.BirthDate
import com.elan.edubridge.shared.domain.model.GuardianConsentStatus

/**
 * 연령 판정의 클라이언트 쪽 사본(§13, MVP 계획 §14 출시 차단 조건).
 *
 * 신규 계정은 **만 14세 이상**만 만들 수 있다. 이 하한은 Play 대상 연령층과 일치하며,
 * 위반은 거부다. 만 14세 미만의 기존 계정은 법정대리인 동의 상태를 계속 해석해야 하므로
 * [GuardianConsentStatus.PENDING_GUARDIAN_CONSENT] 관련 값은 호환성을 위해 유지한다.
 *
 * **이 객체는 인가의 근거가 아니다.** 사용자 문서는 클라이언트가 직접 만들기 때문에
 * 실제 차단은 `firebase/firestore.rules`의 `isEligibleAge()`가 하고, `ageGroup`·
 * `consentStatus` 파생은 서버(`functions/src/profile.ts`의 `onUserProfileWritten`)만
 * 쓴다. 여기 있는 사본의 목적은 하나뿐이다: 가입 화면이 서버에 왕복하기 전에 **같은
 * 기준으로** 미리 알려주는 것. `PermissionPolicy`가 `functions/src/common/auth.ts`의
 * TS 사본과 짝을 이루는 것과 같은 관계이며, 어긋나면 사용자는 통과할 것처럼 보였다가
 * 거부당한다.
 *
 * 나이 계산의 기준일은 호출자가 넘긴다([BirthDate.isOnOrBefore]와 같은 이유로
 * `kotlinx-datetime`을 끌어오지 않는다). 서버와 rules가 모두 **서울 달력일**을 기준으로
 * 하므로 호출자도 그래야 한다 — 자세한 근거는 `functions/src/common/age.ts` 머리말.
 */
object AgePolicy {
    /** 이 나이 미만은 계정을 만들 수 없다. `firestore.rules`와 동일한 값이다. */
    const val MINIMUM_SIGNUP_AGE: Int = 14

    /** 이 나이 미만은 법정대리인 동의가 필요하다(PIPA 제22조의2). */
    const val GUARDIAN_CONSENT_AGE: Int = 14

    /**
     * 만 나이(완료 연수). 생년월일과 기준일 모두 `YYYY-MM-DD`여야 하며, 둘 중 하나라도
     * 해석할 수 없으면 null이다 — "모른다"와 "0세"는 다른 답이다.
     */
    fun ageOn(birthDate: String, today: String): Int? {
        val birth = BirthDate.normalize(birthDate) ?: return null
        val reference = BirthDate.normalize(today) ?: return null
        val birthYear = birth.substring(0, 4).toInt()
        val birthMonth = birth.substring(5, 7).toInt()
        val birthDay = birth.substring(8, 10).toInt()
        val year = reference.substring(0, 4).toInt()
        val month = reference.substring(5, 7).toInt()
        val day = reference.substring(8, 10).toInt()
        val beforeBirthday = month < birthMonth || (month == birthMonth && day < birthDay)
        val age = year - birthYear - if (beforeBirthday) 1 else 0
        return if (age < 0) null else age
    }

    /**
     * 서버가 저장할 [AgeGroup]의 예측값.
     *
     * 해석할 수 없는 생년월일은 [AgeGroup.UNKNOWN]이다 — 어린 쪽으로 추정하지 않는다.
     */
    fun ageGroupOn(birthDate: String, today: String): AgeGroup {
        val age = ageOn(birthDate, today) ?: return AgeGroup.UNKNOWN
        return if (age < GUARDIAN_CONSENT_AGE) AgeGroup.UNDER_14 else AgeGroup.OVER_14
    }

    /** [ageGroupOn]에 따라 서버가 세울 동의 상태. 보호자가 이미 답한 경우는 서버가 보존한다. */
    fun consentStatusFor(ageGroup: AgeGroup): GuardianConsentStatus = when (ageGroup) {
        AgeGroup.UNDER_14 -> GuardianConsentStatus.PENDING_GUARDIAN_CONSENT
        AgeGroup.OVER_14, AgeGroup.UNKNOWN -> GuardianConsentStatus.NOT_REQUIRED
    }

    /**
     * 가입을 거부해야 하는지. 해석할 수 없는 값은 **위반이 아니다** — 형식 오류는
     * [BirthDate.normalize]가 따로 잡고, 여기서 함께 거부하면 두 오류가 한 문구로 뭉개진다.
     */
    fun isBelowSignupAge(birthDate: String, today: String): Boolean {
        val age = ageOn(birthDate, today) ?: return false
        return age < MINIMUM_SIGNUP_AGE
    }

    /**
     * 가입 화면이 물어볼 것 하나로 정리한 결과. 미래 날짜·형식 오류·연령 미달을 구분해
     * 돌려주므로 호출부가 세 경우에 서로 다른 문구를 쓸 수 있다.
     */
    fun validateSignupBirthDate(birthDate: String, today: String): SignupAgeVerdict {
        val normalized = BirthDate.normalize(birthDate) ?: return SignupAgeVerdict.MALFORMED
        if (!BirthDate.isOnOrBefore(normalized, today)) return SignupAgeVerdict.IN_FUTURE
        if (isBelowSignupAge(normalized, today)) return SignupAgeVerdict.TOO_YOUNG
        return SignupAgeVerdict.ELIGIBLE
    }
}

/** [AgePolicy.validateSignupBirthDate]의 결과. 문구는 각 클라이언트가 정한다. */
enum class SignupAgeVerdict {
    ELIGIBLE,

    /** `YYYY-MM-DD`가 아니거나 달력에 없는 날. */
    MALFORMED,

    /** 오늘 이후 날짜. */
    IN_FUTURE,

    /** 만 [AgePolicy.MINIMUM_SIGNUP_AGE]세 미만 — 서버와 rules도 같은 이유로 거부한다. */
    TOO_YOUNG,
}
